// Shared rendering for the trick-taking games (Hearts, Spades, Euchre).
import type { Card } from '@campfire/cards';
import type { Seat } from '@campfire/engine';
import type { CompletedTrick, Trick } from '@campfire/tricks';
import { cardFace, cardLabel } from '../cards.ts';
import { h } from '../dom.ts';
import { name, type TableCtx } from '../games.ts';

export const SUIT_GLYPH: Readonly<Record<string, string>> = { S: '♠', H: '♥', D: '♦', C: '♣' };
export const SUIT_WORD: Readonly<Record<string, string>> = { S: 'Spades', H: 'Hearts', D: 'Diamonds', C: 'Clubs' };

export interface CenterOpts {
  readonly trick: Trick;
  readonly lastTrick: CompletedTrick | null;
  readonly seats: number;
  readonly turn: Seat | null;
  /** Extra tag under a seat's name, e.g. "partner" or "bid 4". */
  readonly tag?: (s: Seat) => string | null;
  readonly sittingOut?: Seat | null;
}

/**
 * The trick in the middle of the table, each card in front of the seat that played it,
 * laid out around the viewer (you at the bottom, the next player on your left).
 * With no card down yet it shows the trick just taken; mid-trick a "Last trick" button peeks at it.
 */
export function trickCenter(ctx: TableCtx, o: CenterOpts): HTMLElement {
  const me = ctx.mySeat ?? 0;
  // Peeking is tied to one particular last trick, so it switches itself off once the next trick is taken.
  const lastKey = o.lastTrick?.plays[0]?.card.id ?? null;
  const peek = lastKey !== null && ctx.local.get<string>('trick-peek') === lastKey && o.trick.plays.length > 0;
  const showing: Trick | CompletedTrick = o.trick.plays.length === 0 && o.lastTrick ? o.lastTrick : peek && o.lastTrick ? o.lastTrick : o.trick;
  const done: Seat | null = 'winner' in showing ? (showing as CompletedTrick).winner : null;
  const byseat = new Map(showing.plays.map((p) => [p.seat, p.card]));
  const slot = (s: Seat) => {
    const r = (s - me + o.seats) % o.seats;
    const a = (r * 2 * Math.PI) / o.seats;
    const x = 50 - 40 * Math.sin(a);
    const y = 48 + 34 * Math.cos(a);
    const card = byseat.get(s);
    const out = o.sittingOut === s;
    const tag = o.tag?.(s);
    return h(
      `div.trick-slot${o.turn === s ? '.turn' : ''}${done === s ? '.won' : ''}${out ? '.out' : ''}`,
      { style: `left:${x.toFixed(1)}%;top:${y.toFixed(1)}%`, 'data-testid': `trick-seat-${s}` },
      h('div.trick-name', {}, name(ctx, s), tag ? h('span.trick-tag', {}, tag) : null),
      out ? h('div.muted', {}, 'sits out') : card ? cardFace(card, { small: true, testid: `trick-card-${s}` }) : h('div.card.small.empty'),
    );
  };
  return h(
    `div.trick-center${showing === o.trick ? '' : '.past'}`,
    { 'data-testid': 'trick' },
    Array.from({ length: o.seats }, (_, s) => slot(s)),
    h(
      'div.trick-caption',
      {},
      done !== null ? h('span', { 'data-testid': 'trick-winner' }, peek ? `Last trick: ${name(ctx, done)} took it` : `${name(ctx, done)} ${done === ctx.you ? 'take' : 'takes'} the trick`) : null,
      o.trick.plays.length > 0 && o.lastTrick
        ? h('button.link', { 'data-testid': 'trick-peek', onclick: () => ctx.local.set('trick-peek', peek ? null : lastKey) }, peek ? 'Back to this trick' : 'Last trick')
        : null,
    ),
  );
}

/** Your hand: legal cards are tappable, the rest dim when it is your turn. */
export function trickHand(
  ctx: TableCtx,
  hand: readonly Card[] | null,
  legal: readonly string[],
  onTap: (c: Card) => void,
  opts: { readonly picked?: readonly string[]; readonly received?: readonly string[]; readonly dimOthers?: boolean } = {},
): HTMLElement | null {
  if (!hand) return null;
  const dimOthers = opts.dimOthers ?? ctx.myTurn;
  return h(
    'div.hand.trick-hand',
    { 'data-testid': 'hand' },
    hand.map((c) => {
      const ok = legal.includes(c.id);
      const el = cardFace(c, { picked: opts.picked?.includes(c.id), onclick: ok ? () => onTap(c) : undefined });
      if (dimOthers && !ok) el.classList.add('dim');
      if (opts.received?.includes(c.id)) el.classList.add('received');
      return el;
    }),
  );
}

/** "2♣" style label for event lines. */
export const label = (c: Card): string => cardLabel(c);
